import { DataTable, Column, Badge, IconButton } from '../../../shared/components';
import { OperationTimeRecord } from '../../../shared/services/operationTimes/types';
import { formatDateTime, formatDuration } from '../../../shared/utils/datetime';
import { EditIcon, TrashIcon } from '../../../shared/icons';

interface ActivityHistoryTableProps {
  rows: OperationTimeRecord[];
  isLoading?: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (record: OperationTimeRecord) => void;
  onDelete: (record: OperationTimeRecord) => void;
}

/** Etiqueta + color de la columna "Modificado / Borrado". El motivo completo se ve al pasar el mouse (hover). */
function NovedadCell({ record }: { record: OperationTimeRecord }) {
  if (record.novedad === 'none') return <span className="text-ink-300">—</span>;
  const isDeleted = record.novedad === 'deleted';
  const label = isDeleted ? 'Borrado' : 'Modificado';
  const reason = record.novedadReason?.trim();
  const title = reason ? `${label}, ${reason}` : label;
  return (
    <span
      title={title}
      className={[
        'inline-flex max-w-[220px] cursor-help items-center truncate rounded-full px-2.5 py-1 text-xs font-semibold',
        isDeleted ? 'bg-orange-200 text-orange-900' : 'bg-amber-100 text-amber-800',
      ].join(' ')}
    >
      {title}
    </span>
  );
}

/** Historial de registros - Funcionalidades: Consultar / Editar (autorizados) / Eliminar (admin). */
export function ActivityHistoryTable({ rows, isLoading, canEdit, canDelete, onEdit, onDelete }: ActivityHistoryTableProps) {
  const columns: Column<OperationTimeRecord>[] = [
    { key: 'code', header: 'Código', render: (r) => <span className="font-mono text-xs text-ink-400">{r.code}</span> },
    { key: 'userName', header: 'Usuario', render: (r) => r.userName, sortable: true, sortValue: (r) => r.userName },
    { key: 'activityName', header: 'Actividad', render: (r) => <Badge tone="success">{r.activityName}</Badge> },
    { key: 'start', header: 'Inicio', render: (r) => formatDateTime(r.startDateTime), sortable: true, sortValue: (r) => r.startDateTime },
    { key: 'end', header: 'Fin', render: (r) => formatDateTime(r.endDateTime) },
    { key: 'duration', header: 'Duración', render: (r) => <span className="font-semibold">{formatDuration(r.durationSeconds)}</span> },
    { key: 'observations', header: 'Observaciones', render: (r) => <span className="text-ink-400">{r.observations || '—'}</span> },
  ];

  // Acciones (editar/eliminar) y "Modificado / Borrado" van en UNA sola
  // columna para que el badge quede alineado en la misma fila que los
  // botones, en vez de en una columna aparte con ancho propio.
  columns.push({
    key: 'actions',
    header: 'Modificado / Borrado',
    render: (r) => (
      <div className="flex items-center gap-2">
        {canEdit && r.novedad !== 'deleted' && (
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => onEdit(r)} className="h-9 w-9" />
        )}
        {canDelete && r.novedad !== 'deleted' && (
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => onDelete(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        )}
        <NovedadCell record={r} />
      </div>
    ),
  });

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      emptyTitle="Sin registros de tiempo"
      emptyDescription="Los registros que guardes aparecerán aquí."
      pageSize={5}
      defaultSortKey="start"
      defaultSortDir="asc"
    />
  );
}
